import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Bell, BellOff, Check, Crown, Laptop, MonitorSmartphone, Moon, Settings2, Volume2, X } from 'lucide-react';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { fullDate, relativeTime } from '../../lib/format';
import { useRing } from '../../lib/ring';
import { useMe } from '../../hooks/useMe';
import { Badge, Button, Card, CardBody, CardHeader, ErrorState, Notice, SkeletonLines, useToast } from '../ui';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <dt className="text-[0.8125rem] text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-[0.8125rem] font-medium text-ink">{children}</dd>
    </div>
  );
}

function YesNo({ ok, yes = 'Yes', no = 'No' }: { ok: boolean; yes?: string; no?: string }) {
  return (
    <span className={clsx('inline-flex items-center gap-1', ok ? 'text-success-ink' : 'text-danger-ink')}>
      {ok ? <Check className="h-4 w-4" aria-hidden /> : <X className="h-4 w-4" aria-hidden />}
      {ok ? yes : no}
    </span>
  );
}

function MicroLabel({ id, icon, children }: { id: string; icon: ReactNode; children: ReactNode }) {
  return (
    <h3 id={id} className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
      {icon}
      {children}
    </h3>
  );
}

/** Live ringing status: this device (web or desktop), the server's directive, and every heartbeating device. */
export function RingStatusPanel() {
  const ring = useRing();
  const me = useMe().data;
  const toast = useToast();
  const [enabling, setEnabling] = useState(false);
  const status = useQuery({ queryKey: qk.ringStatus, queryFn: () => api('GET /api/ring/status'), refetchInterval: 30_000 });
  const d = ring.directive;
  const ds = ring.desktopState;

  const enableSound = async () => {
    setEnabling(true);
    try {
      const ok = await ring.enableSound();
      if (ok) toast.success('Sound enabled in this tab.');
      else toast.error(new Error('The browser blocked audio. Check the tab or system sound settings and try again.'));
    } finally {
      setEnabling(false);
    }
  };

  const perm = ring.notificationPermission;

  return (
    <Card>
      <CardHeader
        icon={<MonitorSmartphone className="h-5 w-5" />}
        title="Ringing status"
        description="Which devices can ring, and who currently holds the ringing lease so only one device rings at a time."
        actions={
          <Link to="/settings/notifications" className="inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-[0.8125rem] font-semibold text-primary-ink transition-colors hover:bg-primary-soft">
            <Settings2 className="h-4 w-4" aria-hidden />
            Notification settings
          </Link>
        }
      />
      <CardBody className="space-y-6 pt-5">
        {/* This device */}
        {ring.mode === 'web' ? (
          <section aria-labelledby="ring-this-tab" className="space-y-3 rounded-2xl border border-hairline bg-raised/50 p-4">
            <MicroLabel id="ring-this-tab" icon={<Laptop className="h-3.5 w-3.5" aria-hidden />}>
              This browser tab
            </MicroLabel>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={ring.soundEnabled ? 'secondary' : 'primary'}
                icon={<Volume2 className="h-4 w-4" />}
                loading={enabling}
                disabled={ring.soundEnabled}
                onClick={() => void enableSound()}
                aria-pressed={ring.soundEnabled}
              >
                {ring.soundEnabled ? 'Sound enabled in this tab' : 'Enable sound in this tab'}
              </Button>
              {perm === 'unsupported' ? (
                <Badge tone="neutral">Notifications not supported in this browser</Badge>
              ) : perm === 'granted' ? (
                <Badge tone="success" icon={<Bell className="h-3 w-3" aria-hidden />}>
                  Notifications allowed
                </Badge>
              ) : perm === 'denied' ? (
                <Badge tone="danger" icon={<BellOff className="h-3 w-3" aria-hidden />}>
                  Notifications blocked — change it in your browser's site settings
                </Badge>
              ) : (
                <Button icon={<Bell className="h-4 w-4" />} onClick={() => void ring.requestNotificationPermission()}>
                  Allow notifications
                </Button>
              )}
            </div>
            {ring.unavailableReason ? (
              <Notice tone="warn">
                <span className="font-semibold">Ringing unavailable here:</span> {ring.unavailableReason}
              </Notice>
            ) : (
              <Notice tone="success" icon={<Check className="h-4 w-4" aria-hidden />}>This tab can ring while it stays open.</Notice>
            )}
            <p className="text-[0.8125rem] leading-relaxed text-muted">Web ringing only works while this tab is open. Install the desktop app for background ringing.</p>
          </section>
        ) : (
          <section aria-labelledby="ring-this-desktop" className="space-y-2 rounded-2xl border border-hairline bg-raised/50 p-4">
            <MicroLabel id="ring-this-desktop" icon={<Laptop className="h-3.5 w-3.5" aria-hidden />}>
              This desktop app
            </MicroLabel>
            {ds ? (
              <dl className="divide-y divide-line/60">
                <Row label="Ringing now">{ds.ringing ? <span className="text-danger-ink">Ringing</span> : 'No'}</Row>
                <Row label="Can ring">
                  <YesNo ok={ds.canRing} />
                </Row>
                {ds.unavailableReason && <Row label="Why not">{ds.unavailableReason}</Row>}
                <Row label="Holds ringing lease">
                  <YesNo ok={ds.holdsLease} />
                </Row>
                {ds.leaseHeldBy && !ds.holdsLease && <Row label="Lease held by">{ds.leaseHeldBy}</Row>}
                <Row label="Pending reminders">{ds.pendingCount}</Row>
                <Row label="Next ring due">{ds.nextDueAt ? <span title={fullDate(ds.nextDueAt)}>{relativeTime(ds.nextDueAt)}</span> : '—'}</Row>
                <Row label="Quiet hours">{ds.quietHoursActive ? 'Active — sound held until they end' : 'Not active'}</Row>
              </dl>
            ) : (
              <SkeletonLines lines={3} />
            )}
            <p className="text-[0.8125rem] leading-relaxed text-muted">The MailPilot tray app keeps running in the background and rings even when all windows are closed.</p>
          </section>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* Server directive (web heartbeat) */}
          {d && (
            <section aria-labelledby="ring-directive" className="space-y-1">
              <MicroLabel id="ring-directive" icon={<Moon className="h-3.5 w-3.5" aria-hidden />}>
                Server schedule
              </MicroLabel>
              <dl className="divide-y divide-line/60">
                <Row label="Quiet hours">
                  {d.quietHoursActive ? (
                    <span className="inline-flex items-center gap-1">
                      <Moon className="h-4 w-4" aria-hidden /> Active
                    </span>
                  ) : (
                    'Not active'
                  )}
                </Row>
                <Row label="Next ring due">{d.nextDueAt ? <span title={fullDate(d.nextDueAt)}>{relativeTime(d.nextDueAt)}</span> : 'Nothing scheduled'}</Row>
                <Row label="Ringing lease held by">{d.leaseHeldBy ?? 'No device'}</Row>
              </dl>
            </section>
          )}

          {/* All devices */}
          <section aria-labelledby="ring-devices" className="space-y-2">
            <MicroLabel id="ring-devices" icon={<MonitorSmartphone className="h-3.5 w-3.5" aria-hidden />}>
              Devices
            </MicroLabel>
            {status.isPending ? (
              <SkeletonLines lines={3} />
            ) : status.isError ? (
              <ErrorState compact error={status.error} onRetry={() => void status.refetch()} title="Couldn't load device status" />
            ) : status.data.devices.length === 0 ? (
              <p className="text-[0.8125rem] leading-relaxed text-muted">No device has checked in yet. Open MailPilot on a device (or install the desktop app) to let it ring.</p>
            ) : (
              <ul className="space-y-0.5">
                {status.data.devices.map((dev) => (
                  <li key={dev.sessionId} className="flex flex-wrap items-start justify-between gap-2 rounded-xl px-3 py-2.5 transition-colors hover:bg-raised">
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 text-[0.8125rem] font-medium text-ink">
                        {dev.deviceName}
                        {me?.sessionId === dev.sessionId && <Badge tone="info">This device</Badge>}
                        {dev.holdsLease && (
                          <Badge tone="primary" icon={<Crown className="h-3 w-3" aria-hidden />}>
                            Holds ringing lease
                          </Badge>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-muted" title={fullDate(dev.lastHeartbeatAt)}>
                        Last heartbeat {relativeTime(dev.lastHeartbeatAt)}
                      </p>
                      {dev.unavailableReason && <p className="mt-1 text-xs text-warn-ink">{dev.unavailableReason}</p>}
                    </div>
                    <span className="text-[0.8125rem]" aria-label={dev.canRing ? 'Can ring' : 'Cannot ring'}>
                      <YesNo ok={dev.canRing} yes="Can ring" no="Can't ring" />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </CardBody>
    </Card>
  );
}
